import React, { FC, useCallback, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import GroupedTaskDisplay from './GroupedTaskDisplay';
import { withUserType } from '../withUserType';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	walkthroughTaskItemsRestCrudThunksForBuyer,
	walkthroughTaskItemsRestCrudThunksForSupplier,
} from '../../thunks/walkthrough_task_items_thunks';

interface IOWTaskComponent {
	existingTasks: any[];
	isAllowedToEdit: boolean;
	siteSurveyTaskTitleId: string;
	updateTask?: (x: any) => any;
	createTask?: (x: any) => any;
	deleteTask?: (x: any[]) => any;
	timeZoneId: any;
	notCheckable?: boolean;
	walkthrough?: any;
	associationDisabled?: boolean;
	addGroupDisabled?: boolean;
	hideIndividualWalkthroughLink: boolean;
}

const WalkthroughTaskSection: FC<IOWTaskComponent> = ({
	existingTasks = [],
	isAllowedToEdit,
	timeZoneId,
	notCheckable,
	walkthrough = {},
	updateTask,
	createTask,
	deleteTask,
	associationDisabled = false,
	addGroupDisabled = true,
	hideIndividualWalkthroughLink,
	siteSurveyTaskTitleId,
}): React.ReactElement => {
	const [currentTasks, setCurrentTasks] = useState<any[]>(existingTasks);

	const walkthroughId = useMemo(() => walkthrough.id, [walkthrough.id]);

	const getNewOrderingId = (tasks) => {
		const newOrderingId =
			tasks.length > 0 ? (Math.max(...tasks.map((_) => _.orderingId)) || 0) + 1 : 0;
		return newOrderingId;
	};

	const groupedTasks = useMemo(() => {
		const grouped = groupBy(currentTasks, (task) => task.groupTitle || '');
		return Object.keys(grouped).map((key) => ({
			groupTitle: key,
			tasks: grouped[key],
		}));
	}, [currentTasks]);

	const onAddNewTask = useCallback(
		(groupTitle) => () => {
			setCurrentTasks((localTasks) => [
				...localTasks,
				...[{ groupTitle, orderingId: getNewOrderingId(localTasks) }],
			]);
		},
		[]
	);

	const onTaskEdit = useCallback(
		(task) => {
			const saveAction = !!task.id ? updateTask : createTask;
			const updatedTask = {
				...task,
				walkthroughId: parseInt(walkthroughId),
				siteSurveyTaskTitleId,
			};
			return saveAction(updatedTask);
		},
		[createTask, siteSurveyTaskTitleId, updateTask, walkthroughId]
	);

	const onTaskDelete = useCallback(
		(task) => {
			if (!task.id) {
				return new Promise((resolve) => resolve(task));
			}

			return deleteTask(task);
		},
		[deleteTask]
	);

	const onSuccess = useCallback((updatedTask) => {
		setCurrentTasks((tasks) => {
			const newTasks = tasks.map((task) =>
				updatedTask.orderingId === task.orderingId ? { ...updatedTask } : { ...task }
			);
			return newTasks;
		});
	}, []);

	const onDeleteSuccess = useCallback((task) => {
		setCurrentTasks((tasks) => {
			const newTasks = tasks.filter((_) => _.orderingId !== task.orderingId);
			return newTasks;
		});
	}, []);

	const onEditTask = useCallback(
		(task) => onTaskEdit(task).then(onSuccess),
		[onSuccess, onTaskEdit]
	);

	const onDeleteTask = useCallback(
		(task) => onTaskDelete(task).then(onDeleteSuccess),
		[onDeleteSuccess, onTaskDelete]
	);

	const groupExists = useMemo(() => groupedTasks.some((_) => !!_.groupTitle), [groupedTasks]);

	const actualTaskCount = useMemo(() => currentTasks.filter((_) => !!_.id).length, [currentTasks]);

	return (
		<div className="flex flex-col">
			<div className="mb-2 flex flex-row items-center gap-2">
				<div className="bodymedium">{`Task List (${actualTaskCount})`}</div>
				<div className="bodyregular materialCard__supportingText">
					Edits to the task list are automatically saved.
				</div>
			</div>
			<GroupedTaskDisplay
				groupedTasks={groupedTasks}
				onEditTask={onEditTask}
				onDeleteTask={onDeleteTask}
				onAddNewTask={onAddNewTask}
				isAllowedToEdit={isAllowedToEdit}
				timeZoneId={timeZoneId}
				notCheckable={notCheckable}
				associationDisabled={associationDisabled}
				addGroupDisabled={addGroupDisabled}
				hideIndividualWalkthroughLink={hideIndividualWalkthroughLink}
			/>
			{isAllowedToEdit ? (
				<div className="padding-less-antd-button mb-4 flex flex-row gap-2 pl-1">
					{groupExists ? null : (
						<div className="">
							<Button onClick={onAddNewTask('')} type="link">
								<PlusOutlined /> Add task
							</Button>
						</div>
					)}
				</div>
			) : null}
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	createTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughTaskItemsRestCrudThunksForSupplier.create(entity)
				: walkthroughTaskItemsRestCrudThunksForBuyer.create(entity)
		),
	updateTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughTaskItemsRestCrudThunksForSupplier.update(entity)
				: walkthroughTaskItemsRestCrudThunksForBuyer.update(entity)
		),
	deleteTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughTaskItemsRestCrudThunksForSupplier.delete(entity, 'id', null, false, true)
				: walkthroughTaskItemsRestCrudThunksForBuyer.delete(entity, 'id', null, false, true)
		),
});

export default withUserType(
	connect(null, mapDispatchToProps)(WalkthroughTaskSection as any) as any
);
